import { useEffect, useRef, useSyncExternalStore } from "react"
import { toast } from "sonner"

import Flash, { FlashAlert, useResolvedFlash } from "@/components/ui/flash"

// Tailwind's `sm` breakpoint; the shell's flash placement switches here.
const SM_QUERY = "(min-width: 40rem)"

const subscribe = (onChange: () => void) => {
  const list = window.matchMedia(SM_QUERY)
  list.addEventListener("change", onChange)
  return () => list.removeEventListener("change", onChange)
}

function useAtLeastSm() {
  return useSyncExternalStore(
    subscribe,
    () => window.matchMedia(SM_QUERY).matches,
    () => true,
  )
}

// The toast stays long enough to be read on a phone and for a browser spec
// to find it after the page settles.
const TOAST_DURATION_MS = 10_000

// Below `sm` the visit's flash is a toast announced as a status; keyed on the
// flash payload so a re-render of the same visit never repeats it, while the
// next delivery, even with the same words, fires again.
//
// The toast is raised on the next task: sonner's Toaster only learns of
// toasts raised after its own subscription, and on a fresh page load this
// effect runs in the same commit, before the Toaster's.
export function useFlashToast(enabled: boolean) {
  const { payload, resolved } = useResolvedFlash()
  const presented = useRef<object | null>(null)

  useEffect(() => {
    if (!enabled || resolved === null || presented.current === payload) return

    presented.current = payload
    const timer = setTimeout(() => {
      toast.custom(() => <FlashAlert role="status" {...resolved} />, {
        duration: TOAST_DURATION_MS,
      })
    })
    return () => clearTimeout(timer)
  }, [enabled, payload, resolved])
}

export function FlashSlot() {
  const atLeastSm = useAtLeastSm()
  useFlashToast(!atLeastSm)

  if (!atLeastSm) return null

  return (
    <div
      data-testid="page-chrome-flash"
      className="container mx-auto px-4 pt-4 empty:hidden"
    >
      <Flash />
    </div>
  )
}
